import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';

export type BottomSheetSize = 'small' | 'medium' | 'full';
export type BottomSheetPosition = 'bottom' | 'top';

@Component({
  selector: 'lib-bottom-sheet',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './bottom-sheet.component.html',
  styleUrls: ['./bottom-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideAnimation', [
      transition('void => *', [
        style({ transform: 'translateY(-100%)' }),
        animate(
          '300ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({ transform: 'translateY(0)' })
        ),
      ]),
      transition('* => void', [
        animate(
          '250ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({ transform: 'translateY(-100%)' })
        ),
      ]),
    ]),
    trigger('slideTopAnimation', [
      transition('void => *', [
        style({ transform: 'translateY(100%)' }),
        animate(
          '300ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({ transform: 'translateY(0)' })
        ),
      ]),
      transition('* => void', [
        animate(
          '250ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({ transform: 'translateY(100%)' })
        ),
      ]),
    ]),
    trigger('fadeAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('200ms', style({ opacity: 0 }))]),
    ]),
  ],
})
export class BottomSheetComponent implements AfterViewInit, OnDestroy {
  @ViewChild('sheetContent', { read: ElementRef })
  sheetContent?: ElementRef<HTMLDivElement>;

  private static _baseZIndex = 1050;
  private static _stackCounter = 0;

  // ========== INPUTS ==========
  @Input() set visible(value: boolean) {
    const wasVisible = this._visible();
    this._visible.set(value);
    if (value && !wasVisible) {
      this.handleOpen();
    } else if (!value && wasVisible) {
      this.handleClose();
    }
  }
  get visible(): boolean {
    return this._visible();
  }

  @Input() set size(value: BottomSheetSize) {
    this._size = value;
    if (!this._hasInputFocused) {
      this._effectiveSize.set(value);
    }
  }
  get size(): BottomSheetSize {
    return this._size;
  }

  @Input() position: BottomSheetPosition = 'bottom';
  @Input() draggable = true;
  @Input() closeOnBackdrop = true;
  @Input() closeOnEscape = true;
  @Input() showHandle = true;
  @Input() modal = true;
  @Input() disableAnimation = false;
  @Input() loading = false;
  @Input() preventClose = false; // Previne fechamento durante loading/processo
  @Input() keyboardAware = true;

  @Input() headerTemplate?: TemplateRef<any>;
  @Input() footerTemplate?: TemplateRef<any>;

  // ========== OUTPUTS ==========
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() show = new EventEmitter<void>();
  @Output() hide = new EventEmitter<void>();
  @Output() confirmed = new EventEmitter<any>();
  @Output() canceled = new EventEmitter<void>();

  // ========== SIGNALS ==========
  private _visible = signal(false);
  $visible = this._visible.asReadonly();

  private _zIndex = signal(BottomSheetComponent._baseZIndex);
  $backdropZIndex = this._zIndex.asReadonly();
  $containerZIndex = computed(() => this._zIndex() + 1);

  private _isDragging = signal(false);
  $isDragging = this._isDragging.asReadonly();

  private _currentTranslateY = signal(0);
  $currentTranslateY = this._currentTranslateY.asReadonly();

  private _effectiveSize = signal<BottomSheetSize>('medium');
  private _isKeyboardExpanded = signal(false);
  $isKeyboardExpanded = this._isKeyboardExpanded.asReadonly();

  // ========== COMPUTED ==========
  $sizeClass = computed(() => `sheet-${this._effectiveSize()}`);
  $positionClass = computed(() => `sheet-${this.position}`);
  $shouldShowHandle = computed(
    () => this.showHandle && this.position === 'bottom'
  );

  // ========== DRAG STATE ==========
  private startY = 0;
  private currentY = 0;
  private originalBodyOverflow = '';
  private focusedElementBeforeOpen?: HTMLElement;
  private focusTrapElements: HTMLElement[] = [];
  private _viewportResizeHandler: (() => void) | null = null;

  // ========== KEYBOARD STATE ==========
  private _size: BottomSheetSize = 'medium';
  private _hasInputFocused = false;
  private _currentFocusedInput: HTMLElement | null = null;
  private _inputFocusinBound: ((e: Event) => void) | null = null;
  private _inputFocusoutBound: ((e: Event) => void) | null = null;
  private _scrollScheduled: ReturnType<typeof setTimeout> | null = null;

  constructor(private elementRef: ElementRef, private cdr: ChangeDetectorRef) {
    // Effect para gerenciar body scroll
    effect(() => {
      if (this._visible()) {
        this.disableBodyScroll();
        this.trapFocus();
        this.cdr.markForCheck();
      } else {
        this.enableBodyScroll();
        this.releaseFocus();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.visible) {
      setTimeout(() => this.trapFocus(), 100);
    }
  }

  ngOnDestroy(): void {
    if (this._visible()) {
      BottomSheetComponent._stackCounter = Math.max(
        0,
        BottomSheetComponent._stackCounter - 2
      );
    }
    this.enableBodyScroll();
    this.releaseFocus();
    this.teardownViewportListener();
  }

  // ========== LIFECYCLE METHODS ==========
  private handleOpen(): void {
    BottomSheetComponent._stackCounter += 2;
    this._zIndex.set(
      BottomSheetComponent._baseZIndex + BottomSheetComponent._stackCounter
    );
    this.show.emit();
    this.focusedElementBeforeOpen = document.activeElement as HTMLElement;
    this.setupViewportListener();
    // Aguarda DOM para sheetContent estar disponível
    setTimeout(() => this.setupInputFocusListeners(), 0);
    this.cdr.markForCheck();
  }

  private handleClose(): void {
    BottomSheetComponent._stackCounter = Math.max(
      0,
      BottomSheetComponent._stackCounter - 2
    );
    this.hide.emit();
    this._currentTranslateY.set(0);
    this.teardownViewportListener();
    this.teardownInputFocusListeners();
    this._hasInputFocused = false;
    this._currentFocusedInput = null;
    this._isKeyboardExpanded.set(false);
    this._effectiveSize.set(this._size);
    this.cdr.markForCheck();
  }

  // ========== INPUT FOCUS LISTENERS ==========
  private setupInputFocusListeners(): void {
    if (!this.keyboardAware || !this.sheetContent || this.position !== 'bottom')
      return;

    const el = this.sheetContent.nativeElement;

    this._inputFocusinBound = (e: Event) => {
      const target = e.target as HTMLElement;
      const isInput = target.matches(
        'input, textarea, select, [contenteditable]'
      );
      if (isInput) {
        this._hasInputFocused = true;
        this._currentFocusedInput = target;
      }
    };

    this._inputFocusoutBound = (e: Event) => {
      const related = (e as FocusEvent).relatedTarget as HTMLElement | null;
      // Só limpa se o foco saiu totalmente do sheet
      if (!related || !el.contains(related)) {
        this._hasInputFocused = false;
        this._currentFocusedInput = null;
      }
    };

    el.addEventListener('focusin', this._inputFocusinBound);
    el.addEventListener('focusout', this._inputFocusoutBound);
  }

  private teardownInputFocusListeners(): void {
    if (!this.sheetContent) return;
    const el = this.sheetContent.nativeElement;
    if (this._inputFocusinBound) {
      el.removeEventListener('focusin', this._inputFocusinBound);
      this._inputFocusinBound = null;
    }
    if (this._inputFocusoutBound) {
      el.removeEventListener('focusout', this._inputFocusoutBound);
      this._inputFocusoutBound = null;
    }
    if (this._scrollScheduled !== null) {
      clearTimeout(this._scrollScheduled);
      this._scrollScheduled = null;
    }
  }

  private scrollFocusedInputIntoView(): void {
    if (!this._currentFocusedInput) return;
    this._scrollScheduled = setTimeout(() => {
      this._currentFocusedInput?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
      this._scrollScheduled = null;
    }, 150);
  }

  // ========== PUBLIC METHODS ==========
  close(): void {
    if (this.preventClose || this.loading || !this._visible()) {
      return;
    }
    this.visible = false;
    this.visibleChange.emit(false);
  }

  confirm(data?: any): void {
    if (this.preventClose || this.loading) {
      return;
    }
    this.confirmed.emit(data);
    this.close();
  }

  cancel(): void {
    if (this.preventClose || this.loading) {
      return;
    }
    this.canceled.emit();
    this.close();
  }

  // ========== DRAG HANDLERS ==========
  onDragStart(event: MouseEvent | TouchEvent): void {
    if (!this.draggable || this.preventClose || this.loading) {
      return;
    }

    this._isDragging.set(true);
    this.startY = this.getClientY(event);
    this.currentY = this.startY;

    // Só previne o default se o evento for cancelável
    if (event.cancelable) {
      event.preventDefault();
    }
  }

  @HostListener('document:mousemove', ['$event'])
  @HostListener('document:touchmove', ['$event'])
  onDragMove(event: MouseEvent | TouchEvent): void {
    if (!this.$isDragging()) {
      return;
    }

    this.currentY = this.getClientY(event);
    const deltaY = this.currentY - this.startY;

    // Apenas permite arrastar para baixo (position: bottom) ou para cima (position: top)
    if (this.position === 'bottom' && deltaY > 0) {
      this._currentTranslateY.set(deltaY);
    } else if (this.position === 'top' && deltaY < 0) {
      this._currentTranslateY.set(Math.abs(deltaY));
    }
  }

  @HostListener('document:mouseup', ['$event'])
  @HostListener('document:touchend', ['$event'])
  onDragEnd(event: MouseEvent | TouchEvent): void {
    if (!this.$isDragging()) {
      return;
    }

    this._isDragging.set(false);

    const deltaY = this.currentY - this.startY;
    const threshold = 150; // Pixels para fechar

    const shouldClose =
      (this.position === 'bottom' && deltaY > threshold) ||
      (this.position === 'top' && deltaY < -threshold);

    if (shouldClose) {
      this.close();
    } else {
      // Reset position com animação
      this._currentTranslateY.set(0);
    }
  }

  // ========== BACKDROP HANDLER ==========
  onBackdropClick(): void {
    if (this.closeOnBackdrop && !this.preventClose && !this.loading) {
      this.close();
    }
  }

  // ========== KEYBOARD HANDLER ==========
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    const kbd = event as KeyboardEvent;
    if (
      this.visible &&
      this.closeOnEscape &&
      !this.preventClose &&
      !this.loading
    ) {
      kbd.preventDefault();
      this.close();
    }
  }

  // ========== FOCUS TRAP ==========
  private trapFocus(): void {
    if (!this.sheetContent) {
      return;
    }

    const focusableElements = this.sheetContent.nativeElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    this.focusTrapElements = Array.from(focusableElements) as HTMLElement[];

    if (this.focusTrapElements.length > 0) {
      this.focusTrapElements[0].focus();
    }
  }

  private releaseFocus(): void {
    if (this.focusedElementBeforeOpen) {
      this.focusedElementBeforeOpen.focus();
      this.focusedElementBeforeOpen = undefined;
    }
  }

  @HostListener('document:keydown.tab', ['$event'])
  onTabKey(event: Event): void {
    const kbd = event as KeyboardEvent;
    if (!this.visible || this.focusTrapElements.length === 0) {
      return;
    }

    const firstElement = this.focusTrapElements[0];
    const lastElement =
      this.focusTrapElements[this.focusTrapElements.length - 1];

    if (kbd.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        kbd.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        kbd.preventDefault();
        firstElement.focus();
      }
    }
  }

  // ========== BODY SCROLL ==========
  private disableBodyScroll(): void {
    this.originalBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = this.getScrollbarWidth() + 'px';
  }

  private enableBodyScroll(): void {
    document.body.style.overflow = this.originalBodyOverflow;
    document.body.style.paddingRight = '';
  }

  private getScrollbarWidth(): number {
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll';
    document.body.appendChild(outer);

    const inner = document.createElement('div');
    outer.appendChild(inner);

    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    outer.parentNode?.removeChild(outer);

    return scrollbarWidth;
  }

  // ========== VISUAL VIEWPORT ==========
  private setupViewportListener(): void {
    if (typeof window === 'undefined' || !window.visualViewport) return;

    const update = () => {
      const vv = window.visualViewport!;
      const el = this.elementRef.nativeElement as HTMLElement;

      el.style.setProperty('--bs-viewport-height', `${vv.height}px`);

      // No iOS innerHeight não muda quando o teclado abre; no Android Chrome muda junto
      // com vv.height, resultando em 0 (browser já posiciona fixed corretamente no Android)
      const keyboardHeight = Math.max(0, window.innerHeight - vv.height);
      el.style.setProperty('--bs-keyboard-height', `${keyboardHeight}px`);

      // Expande para full quando teclado abre com input focado; retrai ao fechar
      if (this.keyboardAware && this.position === 'bottom') {
        if (keyboardHeight > 0 && this._hasInputFocused) {
          if (!this._isKeyboardExpanded()) {
            this._isKeyboardExpanded.set(true);
            this._effectiveSize.set('full');
            this.scrollFocusedInputIntoView();
          }
        } else if (keyboardHeight === 0) {
          if (this._isKeyboardExpanded()) {
            this._isKeyboardExpanded.set(false);
            this._effectiveSize.set(this._size);
          }
        }
      }

      this.cdr.markForCheck();
    };

    update();
    this._viewportResizeHandler = update;
    window.visualViewport.addEventListener(
      'resize',
      this._viewportResizeHandler
    );
    // iOS dispara 'scroll' no visualViewport quando o teclado abre/fecha
    window.visualViewport.addEventListener(
      'scroll',
      this._viewportResizeHandler
    );
  }

  private teardownViewportListener(): void {
    if (
      !this._viewportResizeHandler ||
      typeof window === 'undefined' ||
      !window.visualViewport
    )
      return;
    window.visualViewport.removeEventListener(
      'resize',
      this._viewportResizeHandler
    );
    window.visualViewport.removeEventListener(
      'scroll',
      this._viewportResizeHandler
    );
    this._viewportResizeHandler = null;
    const el = this.elementRef.nativeElement as HTMLElement;
    el.style.removeProperty('--bs-viewport-height');
    el.style.removeProperty('--bs-keyboard-height');
  }

  // ========== UTILS ==========
  private getClientY(event: MouseEvent | TouchEvent): number {
    return event instanceof MouseEvent
      ? event.clientY
      : event.touches[0].clientY;
  }

  get sheetStyles(): any {
    if (!this.$isDragging() || this.$currentTranslateY() === 0) {
      return {};
    }

    const direction = this.position === 'bottom' ? 1 : -1;
    return {
      transform: `translateY(${this.$currentTranslateY() * direction}px)`,
      transition: 'none',
    };
  }
}
